/**
 * Os modais das ações do chat (Fase B e C): agendar o texto, encaminhar a
 * mensagem para outras conversas minhas e virar tarefa nas Minhas Pendências.
 * Cada um só coleta o que falta e devolve ao `aoConfirmar` — quem chama a API
 * é a página.
 */
import { useMemo, useState } from "react";

import { Modal } from "./Dialogos";

const UMA_HORA_MS = 60 * 60 * 1000;

/** `datetime-local` fala a hora do aparelho; o servidor recebe ISO com fuso. */
function paraCampo(data) {
  const d = new Date(data.getTime() - data.getTimezoneOffset() * 60000);
  return d.toISOString().slice(0, 16);
}

export function AgendarModal({ texto, aoFechar, aoConfirmar }) {
  const [quando, setQuando] = useState(() => paraCampo(new Date(Date.now() + UMA_HORA_MS)));
  const [salvando, setSalvando] = useState(false);
  const confirmar = async () => {
    setSalvando(true);
    try {
      await aoConfirmar(new Date(quando).toISOString());
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Modal open titulo="Agendar mensagem" aoFechar={aoFechar}>
      <p className="pchat-ajuda">A mensagem sai sozinha na hora marcada, como se você tivesse enviado.</p>
      <blockquote className="pchat-citacao">{texto}</blockquote>
      <label className="pchat-campo-rotulo">
        Enviar em
        <input className="pchat-busca" type="datetime-local" value={quando} min={paraCampo(new Date())} onChange={(e) => setQuando(e.target.value)} />
      </label>
      <div className="pchat-modal-acoes">
        <button type="button" className="pchat-btn texto" onClick={aoFechar}>Cancelar</button>
        <button type="button" className="pchat-btn enviar texto" disabled={!quando || salvando} onClick={confirmar}>Agendar</button>
      </div>
    </Modal>
  );
}

export function EncaminharModal({ mensagem, conversas, aoFechar, aoConfirmar }) {
  const [busca, setBusca] = useState("");
  const [marcadas, setMarcadas] = useState(new Set());
  const [enviando, setEnviando] = useState(false);
  const lista = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return conversas
      .filter((c) => !c.arquivada_em)
      .filter((c) => !termo || `${c.nome} ${c.batch_code || ""}`.toLowerCase().includes(termo));
  }, [conversas, busca]);
  const alternar = (id) => setMarcadas((atual) => {
    const nova = new Set(atual);
    if (nova.has(id)) nova.delete(id); else if (nova.size < 10) nova.add(id);
    return nova;
  });
  const confirmar = async () => {
    setEnviando(true);
    try {
      await aoConfirmar([...marcadas]);
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Modal open titulo="Encaminhar" aoFechar={aoFechar}>
      <blockquote className="pchat-citacao">{mensagem.texto || `${mensagem.anexos?.length || 0} arquivo(s)`}</blockquote>
      <input className="pchat-busca" placeholder="Buscar conversa" value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar conversa" autoFocus />
      <div className="pchat-pessoas">
        {lista.length === 0 && <small>Nenhuma conversa com esse nome.</small>}
        {lista.map((c) => (
          <button key={c.id} type="button" className={`pchat-pessoa ${marcadas.has(c.id) ? "marcada" : ""}`} onClick={() => alternar(c.id)}>
            <span className="meta"><b>{c.tipo === "lote" && c.batch_code ? `Lote ${c.batch_code}` : c.nome}</b><small>{c.tipo}</small></span>
            <span aria-hidden="true">{marcadas.has(c.id) ? "☑" : "☐"}</span>
          </button>
        ))}
      </div>
      <div className="pchat-modal-acoes">
        <small>{marcadas.size}/10</small>
        <button type="button" className="pchat-btn enviar texto" disabled={marcadas.size === 0 || enviando} onClick={confirmar}>Encaminhar</button>
      </div>
    </Modal>
  );
}

export function TarefaModal({ mensagem, pessoas, eu, aoFechar, aoConfirmar }) {
  const [titulo, setTitulo] = useState((mensagem.texto || "").slice(0, 120));
  const [responsavelId, setResponsavelId] = useState(eu?.usuario_id || "");
  const [prazo, setPrazo] = useState("");
  const [salvando, setSalvando] = useState(false);
  // Tarefa é de quem tem login: o operador do PIN não tem Minhas Pendências.
  const comLogin = pessoas.filter((p) => p.tipo === "usuario" && p.usuario_id);
  const confirmar = async () => {
    setSalvando(true);
    try {
      await aoConfirmar({ titulo: titulo.trim(), responsavelId, prazo: prazo ? new Date(prazo).toISOString() : null });
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Modal open titulo="Virar tarefa" aoFechar={aoFechar}>
      <p className="pchat-ajuda">A tarefa vai para a Agenda de quem vai fazer, com o link desta mensagem. A conversa recebe um aviso.</p>
      <label className="pchat-campo-rotulo">
        Título
        <input className="pchat-busca" value={titulo} maxLength={200} onChange={(e) => setTitulo(e.target.value)} autoFocus />
      </label>
      <label className="pchat-campo-rotulo">
        Quem faz
        <select className="pchat-busca" value={responsavelId} onChange={(e) => setResponsavelId(e.target.value)}>
          {comLogin.map((p) => <option key={p.id} value={p.usuario_id}>{p.usuario_id === eu?.usuario_id ? `${p.nome} (eu)` : p.nome}</option>)}
        </select>
      </label>
      <label className="pchat-campo-rotulo">
        Prazo (opcional)
        <input className="pchat-busca" type="datetime-local" value={prazo} onChange={(e) => setPrazo(e.target.value)} />
      </label>
      <div className="pchat-modal-acoes">
        <button type="button" className="pchat-btn texto" onClick={aoFechar}>Cancelar</button>
        <button type="button" className="pchat-btn enviar texto" disabled={!titulo.trim() || salvando} onClick={confirmar}>Criar tarefa</button>
      </div>
    </Modal>
  );
}
